import React, { useState, useEffect, useLayoutEffect } from 'react';
import cn from 'classnames';
import DesktopCard from './DesktopCard';
import { getDotWidth, getActiveCardWidth } from './utils';

import * as styles from './DesktopLayout.module.scss';

export default function DesktopSlider({ items }) {
  const transitionSpeed = 700;
  const imageOffset = 285;
  const stateSlides = [...items.slice(-1), ...items, ...items.slice(0, 4)];
  const [current, setCurrent] = useState(1);
  const [activeDot, setActiveDot] = useState(1);
  const [disableTransition, setDisableTransition] = useState(true);
  const [disableArrows, setDisableArrows] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [textWidth, setTextWidth] = useState(0);
  const style = {
    '--translateX': `-${translateX}px`,
    '--transitionSpeed': `${transitionSpeed}ms`,
    '--textWidth': `${textWidth}px`,
    '--imageOffset': `${imageOffset}px`,
  };

  const calculateCardOffsetOnResize = () => {
    const activeCardWidth = getActiveCardWidth();
    const calculatedTextWidth = activeCardWidth - imageOffset - 30;
    const offset = activeCardWidth + 20;

    setTextWidth(calculatedTextWidth);

    return offset;
  };

  const calculateTranslate = index => {
    const cardOffset = calculateCardOffsetOnResize();
    const translateOffset = (stateSlides.length - stateSlides.slice(index).length) * cardOffset;
    setTranslateX(translateOffset);
  };

  useLayoutEffect(() => {
    calculateTranslate(1);
  }, []);

  useEffect(() => {
    const calculateTranslateOnResize = () => {
      setDisableTransition(true);
      calculateTranslate(current);
    };

    window.addEventListener('resize', calculateTranslateOnResize);

    return () => {
      window.removeEventListener('resize', calculateTranslateOnResize);
    };
  }, [current]);

  useEffect(() => {
    if (disableArrows) {
      setTimeout(() => {
        setDisableArrows(false);
      }, transitionSpeed);
    }
  }, [disableArrows]);

  useEffect(() => {
    if (current === stateSlides.length - 3) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(2);
        calculateTranslate(2);
      }, transitionSpeed);
    }
    if (current === stateSlides.length - 4) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(1);
        calculateTranslate(1);
      }, transitionSpeed);
    }
    if (current === 0) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(stateSlides.length - 5);
        calculateTranslate(stateSlides.length - 5);
      }, transitionSpeed);
    }
  }, [current]);

  const handleActiveDotChange = index => {
    if (index === stateSlides.length - 3) {
      setActiveDot(2);
      return;
    }
    if (index === stateSlides.length - 4) {
      setActiveDot(1);
      return;
    }
    if (index === 0) {
      setActiveDot(items.length);
      return;
    }

    setActiveDot(index);
  };

  const handleClick = index => {
    if (index === current) {
      return;
    }

    setDisableArrows(true);

    if (disableTransition) {
      setDisableTransition(false);
    }

    calculateTranslate(index);
    handleActiveDotChange(index);
    setCurrent(index);
  };

  const handleNext = () => {
    handleClick(current + 1);
  };

  const handlePrev = () => {
    handleClick(current - 1);
  };

  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  function handleTouchStart(e) {
    setTouchStart(e.targetTouches[0].clientX);
  }

  function handleTouchMove(e) {
    setTouchEnd(e.targetTouches[0].clientX);
  }

  function handleTouchEnd() {
    if (touchStart - touchEnd > 150) {
      handleNext();
    }

    if (touchStart - touchEnd < -150) {
      handlePrev();
    }
  }

  return (
    <>
      <div id="cardsWrapper" className={styles.cardsWrapper}>
        <div
          className={cn(styles.cardsTrack, !disableTransition && styles.withTransition)}
          style={style}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {stateSlides.map((item, i) => (
            <DesktopCard
              key={i}
              disableTransition={disableTransition}
              active={i === current}
              handleClick={() => handleClick(i)}
              {...item}
            />
          ))}
        </div>
      </div>
      <button
        className={cn(styles.navArrow, styles.prev)}
        onClick={handlePrev}
        disabled={disableArrows}
        aria-label="Previous Slide"
      />
      <button
        className={cn(styles.navArrow, styles.next)}
        onClick={handleNext}
        disabled={disableArrows}
        aria-label="Next Slide"
      />
      <div className={styles.pagination}>
        {stateSlides.map((_, i) => {
          if (i === 0 || i > items.length) {
            return null;
          }
          const dotSize = getDotWidth(items.length, activeDot - 1, i - 1);

          return (
            <button
              key={i}
              className={cn(
                styles.paginationButton,
                i === activeDot && styles.active,
                styles[dotSize],
              )}
              onClick={() => handleClick(i)}
              aria-label={`Pagination Button ${i}`}
            >
              {i}
            </button>
          );
        })}
      </div>
    </>
  );
}
